import { ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { RenderProps } from "./render-types";
import { RichDoc } from "./rich-doc";

type Props = RenderProps & {
  notionPayload: unknown[];
  staleWarning: boolean;
  empty: boolean;
  syncedAt: string | null;
  sourceUrl: string | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

function relativeFromIso(iso: string | null): string {
  if (!iso) return "agora";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "agora";
  const diffSec = Math.round((Date.now() - ts) / 1000);
  if (diffSec < 60) return `há ${Math.max(diffSec, 1)}s`;
  if (diffSec < 3600) return `há ${Math.round(diffSec / 60)}min`;
  if (diffSec < 86_400) return `há ${Math.round(diffSec / 3600)}h`;
  return `há ${Math.round(diffSec / 86_400)}d`;
}

export function NotionMirror({
  notionPayload,
  staleWarning,
  empty,
  syncedAt,
  sourceUrl,
  onRefresh,
  isRefreshing,
}: Props) {
  if (empty) {
    return (
      <div className="rounded-lg border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
        Conteúdo indisponível. Tente novamente em instantes.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {sourceUrl && (
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
          <div className="text-xs text-muted-foreground">
            Sincronizado do Notion · {relativeFromIso(syncedAt)}
          </div>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="h-7 gap-1 px-2 text-xs"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                {isRefreshing ? "Sincronizando…" : "Sincronizar agora"}
              </Button>
            )}
            <a
              href={sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Abrir no Notion
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      )}
      {staleWarning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Mostrando a última versão em cache — a sincronização com o Notion falhou. Tentaremos
          automaticamente em instantes.
        </div>
      )}
      <RichDoc contentBlocks={notionPayload} />
    </div>
  );
}
